'use client';

import * as Checkbox from '@radix-ui/react-checkbox';
import * as Tabs from '@radix-ui/react-tabs';
import { DynamicIcon } from 'phosphor-icons/react';
import { useState } from 'react';
import { c } from '../../../utils/tailwind-merge';
import { ButtonV2 } from '../../atoms/Button/Button.v2';
import { IconButton } from '../../atoms/Button/IconButton';
import { AccountTypeTabs } from '../../molecules/AccountTypeTabs/AccountTypeTabs';
import { FormModal } from '../../molecules/FormModal/FormModal';
import type {
  ChangeFavoriteListModalProps,
  ListData,
} from './ChangeFavoriteListModal.types';
import { CheckboxIndicator } from './CheckboxIndicator';

type ListItemProps = {
  list: ListData;
  isSelected: boolean;
  onToggle: (listId: string) => void;
  microcopy: ChangeFavoriteListModalProps['microcopy'];
};

const ListItem = ({ list, isSelected, onToggle, microcopy }: ListItemProps) => {
  const visibilityIcon =
    list.type === 'private' ? 'Lock' : 'GlobeHemisphereWest';
  const visibilityLabel =
    list.type === 'private' ? microcopy.private : microcopy.public;

  const ariaLabel = `${list.name}, ${list.count} ${microcopy.assets}, ${visibilityLabel}${list.default ? `, ${microcopy.default}` : ''}`;

  return (
    <Checkbox.Root
      checked={isSelected}
      onCheckedChange={() => onToggle(list.id)}
      aria-label={ariaLabel}
      className={c(
        'flex w-full cursor-pointer items-center gap-1 rounded-lg border p-2',
        'transition-colors focus-visible:outline-none focus-visible:ring-2',
        'focus-visible:ring-blue focus-visible:ring-offset-2 focus-visible:ring-offset-grey-900',
        isSelected
          ? 'border-2 border-blue bg-grey-800/30'
          : 'border border-grey-700 hover:border-grey-500'
      )}
    >
      <CheckboxIndicator isSelected={isSelected} />
      <div className="-space-y-[2px] text-left">
        <span className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-base font-medium leading-6 tracking-tight text-grey-100">
          {list.name}
        </span>
        <div className="flex items-center gap-2 text-xs text-grey-400">
          <span>
            {list.count} {microcopy.assets}
          </span>
          <span>•</span>
          <div className="flex items-center gap-1">
            <DynamicIcon
              iconName={visibilityIcon}
              size={12}
              aria-hidden="true"
              focusable="false"
              className="text-grey-400"
            />
            <span>{visibilityLabel}</span>
          </div>
          {list.default && (
            <>
              <span>•</span>
              <span>{microcopy.default}</span>
            </>
          )}
        </div>
      </div>
    </Checkbox.Root>
  );
};

type ListContentProps = {
  lists: ListData[];
  selectedListIds: Set<string>;
  onToggle: (listId: string) => void;
  microcopy: ChangeFavoriteListModalProps['microcopy'];
};

const ListContent = ({
  lists,
  selectedListIds,
  onToggle,
  microcopy,
}: ListContentProps) => {
  return (
    <div className="flex flex-col gap-3">
      {lists.map((listItem) => (
        <ListItem
          key={listItem.id}
          list={listItem}
          isSelected={selectedListIds.has(listItem.id)}
          onToggle={onToggle}
          microcopy={microcopy}
        />
      ))}
    </div>
  );
};

export const ChangeFavoriteListModal = ({
  list,
  multiTabs,
  initialSelectedListIds = [],
  userName,
  userProfilePicture,
  publisherName,
  microcopy,
  onNewList,
  onSave,
  trigger,
  className = '',
}: ChangeFavoriteListModalProps) => {
  const [selectedListIds, setSelectedListIds] = useState<Set<string>>(
    new Set(initialSelectedListIds)
  );
  const [currentTab, setCurrentTab] = useState<'personal' | 'organization'>(
    'personal'
  );

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setSelectedListIds(new Set(initialSelectedListIds));
      setCurrentTab('personal');
    }
  };

  const toggleListSelection = (listId: string) => {
    setSelectedListIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(listId)) {
        newSet.delete(listId);
      } else {
        newSet.add(listId);
      }
      return newSet;
    });
  };

  const handleSave = () => {
    const selectedLists = list.filter((item) => selectedListIds.has(item.id));
    onSave(selectedLists);
  };

  const hasSelectionChanged = () => {
    const initialSet = new Set(initialSelectedListIds);

    if (selectedListIds.size !== initialSet.size) {
      return true;
    }

    for (const id of Array.from(selectedListIds)) {
      if (!initialSet.has(id)) {
        return true;
      }
    }

    return false;
  };

  const personalLists = list.filter((item) => item.personal === true);
  const organizationLists = list.filter((item) => item.personal === false);

  const triggerElement = trigger ?? (
    <IconButton
      className="h-12 w-12 overflow-hidden rounded-lg bg-grey-700 hover:bg-grey-800"
      iconName="BookmarkSimple"
      ariaLabel={microcopy.add}
      iconWeight="bold"
      iconSize={24}
      iconClassName="fill-white hover:fill-white"
    />
  );

  const isSaveDisabled = !hasSelectionChanged();

  const modalContent = (
    <div className="flex flex-1 flex-col pt-4">
      {multiTabs ? (
        <Tabs.Root
          value={currentTab}
          onValueChange={(value) =>
            setCurrentTab(value as 'personal' | 'organization')
          }
          className="flex flex-1 flex-col"
        >
          <AccountTypeTabs
            userName={userName || ''}
            userProfilePicture={userProfilePicture}
            publisherName={publisherName || ''}
            value={currentTab}
            microcopy={{
              ariaLabel: microcopy.tabsAriaLabel,
              personal: microcopy.personal,
              organization: microcopy.organization,
            }}
            onValueChange={(value) => setCurrentTab(value)}
          />

          <Tabs.Content value="personal" className="flex-1 py-3">
            <ListContent
              lists={personalLists}
              selectedListIds={selectedListIds}
              onToggle={toggleListSelection}
              microcopy={microcopy}
            />
          </Tabs.Content>

          <Tabs.Content value="organization" className="flex-1 py-3">
            <ListContent
              lists={organizationLists}
              selectedListIds={selectedListIds}
              onToggle={toggleListSelection}
              microcopy={microcopy}
            />
          </Tabs.Content>
        </Tabs.Root>
      ) : (
        <div className="flex-1">
          <ListContent
            lists={list}
            selectedListIds={selectedListIds}
            onToggle={toggleListSelection}
            microcopy={microcopy}
          />
        </div>
      )}

      <footer className="sticky bottom-0 z-10 -mx-5 mt-1 border-t border-grey-800 bg-grey-900 p-4">
        <div className="flex flex-col gap-3 md:flex-row md:justify-between">
          <ButtonV2
            type="button"
            label={microcopy.new}
            variant="secondary"
            size="medium"
            shape="square"
            iconLeft="Plus"
            onClick={onNewList}
            className="order-2 w-full uppercase md:order-1 md:flex-1 md:flex-shrink-0"
            iconClassName="!mr-2"
          />

          <ButtonV2
            type="button"
            label={microcopy.save}
            variant="primary"
            size="medium"
            shape="square"
            disabled={isSaveDisabled}
            onClick={handleSave}
            className="order-1 w-full uppercase disabled:border-grey-800 disabled:bg-grey-800 disabled:text-grey-600 md:order-2 md:flex-1 md:flex-shrink-0"
          />
        </div>
      </footer>
    </div>
  );

  return (
    <FormModal
      dark
      size="xsmall"
      trigger={triggerElement}
      title={microcopy.title}
      subtitle={microcopy.subTitle}
      close={microcopy.close}
      onOpenChange={handleOpenChange}
      content={modalContent}
      className={c('h-auto', className)}
    />
  );
};

ChangeFavoriteListModal.displayName = 'ChangeFavoriteListModal';
